import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faArrowLeft, faUserClock, faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../shared/notify";

import { MeasurementsDetails } from "shared/components/measurements/measuerement-review";
import AddShot from "./components/add-shot";
import BulkUpload from "./components/bulk-upload";
import TaskNav from "../task-nav/task-nav";
import QANav from "../task-nav/qa-nav";
import ImageItem from "./image-item";
import HeaderUpc from "../h1upc";
import PegholeCapture from "./components/peghole";
import BatchDownload from "./components/batch-download";
import Status from "./components/status";
import NotificationBar from "../../components/notification-bar";
import TaskMessages from "../../components/notification-bar/task-messages";
import NoteLauncher from "../notes";
import LocaleList from "./components/locale-list";
import DuplicateCheck from "../duplicate-check";

import { DEFAULT_GENERIC_ERROR } from "../../shared/constants";
import { getPhotos, getRejectReasons, getShotTemplate, redoPhotography } from "./lib/imageData";
import { RedoTaskButton } from "../../shared/components/redo-button";
import { completeTask, validateTask, validateTaskReject } from "../../shared/taskState";
import { TaskContext } from "../../contexts/task";
import { VALID_DPI } from "./lib/constants";
import { sendNote } from "../notes/lib/data";
import { scrollToId } from "../../shared/helpers";
import { DEFAULT_LOCALE, EDITING_SOURCE_MAP } from "../photography/lib/helpers";
import { updateShot as updateShotProperty, saveGs1Code, rotateImage } from "../photography/lib/photoData";
import { useAlert } from "hooks/useAlert";
import { SettingsContext } from "contexts/settings";
import { SidePanel } from "../side-panel/side-panel";
import { useSidePanel } from "shared/hooks/use-side-panel";
import PhotographyLookups from "../../contexts/photography/photography";
import { matchPath, useLocation, useNavigate, useParams } from "react-router-dom";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";
import { TaskStatus } from "shared/constants/task-status";
import "./image-editing.scss";
import { useShipmentProductFeatures } from "../../shared/hooks/use-shipment-product-features";
import { cesFeatureGuids } from "../../contexts/settings/lib/constants";

const getStatePayload = (state) => {
    if (state === TaskStatus.TO_DO || state === TaskStatus.REDO) {
        return {
            label: "Sent to Client",
            icon: faUserClock,
            promptText: "Please confirm you sent to client.",
            payload: {
                state: "waiting-for-ca",
                data: "sentForCA",
            },
        };
    } else if (state === TaskStatus.WAITING_FOR_CA) {
        return {
            label: "Client Approved",
            icon: faThumbsUp,
            promptText: "Please confirm the client has approved.",
            payload: {
                state: "completed",
                data: "clientApproved",
            },
        };
    }
};

const ImageEditingIndex = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isQA = !!matchPath("/image-editing-qa/:id/:wip", pathname);
    const params = useParams();
    const { toggleAlert } = useAlert();
    const { task } = useContext(TaskContext);
    const { settings } = useContext(SettingsContext);
    const [defaultImageView, setDefaultImageView] = useState("");
    const [hasPegholeData, setHasPegholeData] = useState(false);
    const [isBusy, setIsBusy] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [shots, setShots] = useState([]);
    const [rejectReasons, setRejectReasons] = useState([]);
    const rawGroupShots = []; // Populated from an API call later
    const { hasMobileBanner } = useSidePanel();
    const taskId = params.id;
    const wipKey = params.wip;
    const { featureName: costcoFeatureName } = useShipmentProductFeatures(cesFeatureGuids.COSTCOSPSUP, task.context.shipmentProductKey);

    const isVOUploadImage = useViewOnlyPermissions("UploadImage", task.sourcePage);
    const isVODownloadImage = useViewOnlyPermissions("DownloadImage", task.sourcePage);
    const isVOUploadBulk = useViewOnlyPermissions("UploadBulk", task.sourcePage);
    const isVODownloadBulk = useViewOnlyPermissions("DownloadBulk", task.sourcePage);
    const isVOCompleteTask = useViewOnlyPermissions("CompleteTask", task.sourcePage);
    const isVORejectTask = useViewOnlyPermissions("RejectTask", task.sourcePage);
    const isVORedoPhotography = useViewOnlyPermissions("RedoTask", task.sourcePage, "photography");
    const isVOSkipEditing = useViewOnlyPermissions("SkipEditing", task.sourcePage);
    const isVOPegholes = useViewOnlyPermissions("MeasurePegholes", task.sourcePage);
    const isVONotes = useViewOnlyPermissions("CreateNote", task.sourcePage);

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert({ group: isQA ? "ImageEditingQA" : "ImageEditing" });
        }
    }, [settings.globalNotifications.messages.length]);

    useEffect(() => {
        async function init() {
            await getUneditedShots();
            await getRejectionReasons();
        }

        init();
    }, []);

    const refreshData = async () => {
        await getUneditedShots();
    };

    const getUneditedShots = async () => {
        const template = await getShotTemplate(wipKey);
        const response = await getPhotos(wipKey);

        if (!response) {
            return;
        }

        setIsBusy(false);
        setShots(response.sort((img) => (img.imageView && img.isGroupShot ? -1 : 1)));
        setDefaultImageView(template.defaultImageView);
    };

    const getRejectionReasons = async () => {
        const reasons = await getRejectReasons();

        setRejectReasons(reasons);
    };

    const removeShotByKey = (shotKey) => {
        setShots(shots.filter((s) => s.key !== shotKey));
    };

    const onComplete = () => {
        const backUrl = isQA ? "/task-list/imageeditingqa" : "/task-list/imageediting";
        navigate(backUrl);
    };

    const updateDirtyFlag = (value) => {
        setIsDirty(value);
    };

    const updateHasPegholeData = (value) => {
        setHasPegholeData(value);
    };

    const rotateShot = async (key, rotate) => {
        setIsRotating(true);
        const response = await rotateImage(wipKey, key, rotate, 2);

        if (!response) {
            return;
        }

        notify.success("Rotation Saved");

        setIsRotating(false);
    };

    const updateShot = (shot) => {
        setShots((prevShots) => prevShots.map((s) => (s.key === shot.key ? shot : s)));
    };

    const addShot = (shot) => {
        setShots([shot, ...shots]);
    };

    const changeShotProperty = async (photoWipKey, shotKey, name, value) => {
        const response = await updateShotProperty(photoWipKey, taskId, shotKey, task.sourcePage, {
            [name]: value,
        }).catch((err) => {
            notify.error(err);
        });

        if (!response) {
            return;
        }

        notify.success("Saved");

        const updateShots = [...shots];
        updateShots.map((s) => {
            if (s.key === shotKey) {
                s[name] = value;
            }

            return s;
        });

        setShots(updateShots);
    };

    const changeGs1Code = async (formData, key) => {
        const payload = {
            gs1ImageTypeId: formData.imageType,
            gs1ImageFacingId: formData.imageFacing,
            gs1ImageAngleId: formData.imageAngle,
            gs1ImageStateId: formData.imageState,
        };
        const response = await saveGs1Code(key, payload);

        if (!response) {
            return;
        }

        notify.success("GS1 Code Saved");
        const updateShots = shots.map((shot) => {
            if (shot.key === response.shotKey) {
                return {
                    ...shot,
                    gs1MetadataShortDescription: response.shortDescription,
                    hasGs1Metadata: !!response.shortDescription,
                };
            }

            return shot;
        });
        setShots(updateShots);
    };

    const handleUpdateRejectReason = (shotKey, value, reasonDescription, reasonText) => {
        const updateShots = shots.map((shot) => {
            if (shot.key === shotKey) {
                return {
                    ...shot,
                    editingJobFileRejectTypeId: value,
                    editingJobCustomRejectText: reasonDescription,
                    editingJobRejectText: reasonText,
                };
            }

            return shot;
        });

        setShots(updateShots);
    };

    const handleRemoveRejectReason = (shotKey) => {
        const newShots = shots.map((shot) => {
            if (shot.key === shotKey) {
                return {
                    ...shot,
                    editingJobFileRejectTypeId: 0,
                    editingJobRejectText: null,
                };
            }

            return shot;
        });

        setShots(newShots);
    };

    const validate = async () => {
        if (isBusy) {
            notify.error("Page is currently processing, please try again later.");

            return false;
        }

        const missingUploads = [];
        const rejectedShots = [];

        if (hasMobileBanner && isQA) {
            const heroShots = shots.some((s) => s.imageView === "MHB");

            if (!heroShots) {
                const confirmHero = await notify.confirm(
                    "This customer is eligible for Verified Mobile Hero image creation. Are you sure you want to complete this task without this image?"
                );

                if (!confirmHero) {
                    return notify.error("Product missing Mobile Hero with Banner. Reject back to Photography for corrections.");
                }
            }
        }

        shots.forEach((s) => {
            const localeText = s.locale || DEFAULT_LOCALE.text;

            if (s.editingSourceId !== EDITING_SOURCE_MAP["skipEditing"] && !s.editedBlobKey) {
                missingUploads.push(`- ${s.imageViewDescription} (${localeText})`);
            } else if ((s.uneditedDPIValue && s.uneditedDPIValue !== VALID_DPI) || (s.editedDPIValue && s.editedDPIValue !== VALID_DPI)) {
                missingUploads.push(`- ${s.imageViewDescription} (${localeText})`);
            }

            if (s.editingJobFileRejectTypeId) {
                rejectedShots.push(`- ${s.imageViewDescription} (${localeText})`);
            }
        });

        if (rejectedShots.length > 0) {
            notify.error(`You have rejected shots:<br />${rejectedShots.join("<br />")}<br /><br />You can only reject this task. `);

            return false;
        }

        if (missingUploads.length > 0) {
            notify.error(`Invalid uploads present:<br />${missingUploads.join("<br />")}`);

            return false;
        }

        return await validateTask(taskId);
    };

    const validateReject = async (override = false) => {
        const payload = {
            changeData: {
                rejectedShots: shots.reduce((arr, s) => {
                    if (s.editingJobFileRejectTypeId) {
                        arr.push({
                            shotKey: s.key,
                            rejectedTypeId: s.editingJobFileRejectTypeId,
                        });
                    }

                    return arr;
                }, []),
            },
        };

        if (task.context.linkedTaskContextKey) {
            const confirm = await notify.confirm(
                "This is a Linked product digital lite to digital update. It will be sent to Bright River.<br /><br />Are you sure you want to reject this item?"
            );

            if (!confirm) {
                return false;
            }
        }

        const hasRejectReason = shots.some((s) => !!s.editingJobFileRejectTypeId);

        if (!hasRejectReason) {
            const confirm = await notify.confirm(
                "This product will not go to Bright River without a reject reason.<br /><br />Are you sure you want to reject?"
            );

            if (!confirm) {
                return false;
            }
        }

        const serverValidate = await validateTaskReject(taskId, payload, override);

        if (!serverValidate) {
            return false;
        }

        if (!override && serverValidate.overridableErrors && serverValidate.overridableErrors.length > 0) {
            const displayErrors = serverValidate.overridableErrors.join(", ");
            const confirmOverride = await notify.confirm(`${displayErrors}`);

            if (confirmOverride) {
                return validateReject(true);
            } else {
                return false;
            }
        }

        return true;
    };

    const onRedoClickHandler = async (e) => {
        e.preventDefault();
        setIsBusy(true);

        const prompt = await notify.prompt("Reason for redo:");

        if (prompt.length <= 0) {
            notify.error("A reason must be provided to redo a task");
            setIsBusy(false);

            return;
        }

        if (!prompt) {
            setIsBusy(false);

            return;
        }

        const taskRedoTask = await redoPhotography(task.taskContextKey, task.sourcePage);

        if (!taskRedoTask) {
            setIsBusy(false);

            return;
        }

        if (!taskRedoTask.success) {
            // This is how the errors are reported to the system, so let's show something meaningful here
            notify.error(taskRedoTask[0] || DEFAULT_GENERIC_ERROR);
            setIsBusy(false);

            return;
        }

        const rejectComment = await sendNote({
            items: [
                {
                    noteText: prompt,
                    noteType: "Product",
                    shipmentKey: task.context.shipmentKey,
                    shipmentProductKey: task.context.shipmentProductKey,
                    taskType: "photography.standard",
                },
            ],
        });
        const completionResponse = await completeTask(taskId, true);

        if (!rejectComment || !completionResponse) {
            setIsBusy(false);

            return;
        }

        notify.success("Task has been marked for redo");

        // Redirect based on if QA or not
        if (isQA) {
            navigate("/task-list/imageeditingqa");
        } else {
            navigate("/task-list/imageediting");
        }
    };

    const imageClick = (shotKey) => {
        scrollToId(`shot_${shotKey}`);
    };

    const shotHtml =
        !shots || shots.length === 0
            ? "No images to edit"
            : shots.map((shot) => {
                  return (
                      <ImageItem
                          key={`unedited_${wipKey}_${shot.key}`}
                          taskKey={taskId}
                          wipKey={wipKey}
                          hasPegholeData={hasPegholeData}
                          mimeType={shot.uneditedMimeType}
                          shot={shot}
                          rotateShot={rotateShot}
                          updateShot={updateShot}
                          changeShotProperty={changeShotProperty}
                          changeGs1Code={changeGs1Code}
                          updateRejectReason={handleUpdateRejectReason}
                          isRotating={isRotating}
                          isCompleted={task.isCompleted}
                          showEcom={task.requiresEcom}
                          defaultImageView={defaultImageView}
                          removeShotByKey={removeShotByKey}
                          groupShots={rawGroupShots}
                          shipmentKey={task.shipmentKey}
                          shipmentProductKey={task.shipmentProductKey}
                          rejectReasons={rejectReasons}
                          onRemoveRejectReason={handleRemoveRejectReason}
                          sourcePage={task.sourcePage}
                          isQA={isQA}
                          readOnlyUpload={isVOUploadImage}
                          readOnlyDownload={isVODownloadImage}
                          readOnlySkipEditing={isVOSkipEditing}
                          isIE
                      />
                  );
              });
    const frontView = shots.find((shot) => shot.imageViewDescription === "Front" && shot.editingSourceId !== EDITING_SOURCE_MAP["skipEditing"]);
    const peghole =
        isQA && frontView && !task.isCompleted ? (
            <PegholeCapture
                wipKey={wipKey}
                shot={frontView}
                updateDirtyFlag={updateDirtyFlag}
                updateHasPegholeData={updateHasPegholeData}
                sourcePage={task.sourcePage}
                isReadOnly={isVOPegholes}
            />
        ) : null;
    const redoButtonClasses =
        isBusy || isVORedoPhotography
            ? {
                  className: "button disabled",
                  disabled: "disabled",
              }
            : { className: "button" };

    const acceptPayload = isQA && task.context.imagesNeedClientApproval ? getStatePayload(task.state) : null;

    const rejectPayload = {
        state: "completed",
        data: "rejected",
        changeData: {
            rejectedShots: shots.reduce((arr, s) => {
                if (s.editingJobFileRejectTypeId) {
                    arr.push({
                        shotKey: s.key,
                        imageEditingRejectInfo: {
                            rejectedTypeId: s.editingJobFileRejectTypeId,
                            customRejectText: s.editingJobCustomRejectText,
                        },
                    });
                }

                return arr;
            }, []),
        },
    };

    // FUTURE: Perhaps we remove this and allow multiple shots codes per locale Ex: (Montage)
    const usedShots = shots
        .filter((s) => !s.localeCode || s.localeCode === DEFAULT_LOCALE.value)
        .map((s) => {
            return s.imageView;
        });

    return (
        <PhotographyLookups
            includeMultipleImageEligibleItems={false}
            taskContextKey={task.taskContextKey}
        >
            <div className="flex">
                <SidePanel />
                <div className="container flex-1">
                    <TaskNav
                        taskKey={taskId}
                        hasIMDropboxContent={task.hasIMDropboxContent}
                        isSampleProduct={task.isSampleUpc}
                        linkedTaskContextKey={task.context.linkedTaskContextKey}
                    >
                        <div className="button-group -right">
                            <HeaderUpc />

                            {!task.isCompleted && (
                                <AddShot
                                    shots={shots}
                                    usedShots={usedShots}
                                    addShot={addShot}
                                    wipKey={wipKey}
                                    taskKey={taskId}
                                    sourcePage={task.sourcePage}
                                    isReadOnly={isVOUploadImage}
                                />
                            )}

                            {!task.isCompleted && (
                                <BulkUpload
                                    wipKey={wipKey}
                                    refreshData={refreshData}
                                    sourcePage={task.sourcePage}
                                    isReadOnly={isVOUploadBulk}
                                />
                            )}

                            {task.context.matchedProductTaskContextKey && <DuplicateCheck matchType={task.context.productMatchType} />}
                            <NoteLauncher
                                settings={{
                                    shipmentKey: task.context.shipmentKey,
                                    shipmentProductKey: task.context.shipmentProductKey,
                                }}
                                clientCode={task.context.cxHClientCode}
                                binTrackingNumber={[task.context.binTrackingNumber]}
                                noteType="Product"
                                taskType="imageediting.standard"
                                sourcePage={task.sourcePage}
                                isReadOnly={isVONotes}
                            />
                            {!task.isCompleted && (
                                <RedoTaskButton
                                    onClick={onRedoClickHandler}
                                    {...redoButtonClasses}
                                    title="Redo Photography"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} /> <FontAwesomeIcon icon={faCamera} />
                                </RedoTaskButton>
                            )}

                            <BatchDownload
                                wipKey={wipKey}
                                upc={task.upc}
                                taskKey={taskId}
                                sourcePage={task.sourcePage}
                                isReadOnly={isVODownloadBulk}
                            />

                            {!task.isCompleted && (
                                <div style={{ display: "flex" }}>
                                    <QANav
                                        taskId={taskId}
                                        onComplete={onComplete}
                                        validate={validate}
                                        validateReject={validateReject}
                                        isQA={isQA}
                                        isDirty={isDirty}
                                        taskType="imageediting.standard"
                                        acceptPayload={acceptPayload}
                                        rejectPayload={rejectPayload}
                                        sourcePage={task.sourcePage}
                                        isReadOnly={isVOCompleteTask}
                                        isReadOnlyReject={isVORejectTask}
                                    />
                                </div>
                            )}
                        </div>
                    </TaskNav>
                    {costcoFeatureName && isQA && (
                        <div className="m-l-12">
                            <NotificationBar
                                className="m-l-32"
                                variant="small"
                                type="info"
                            >
                                {costcoFeatureName}
                            </NotificationBar>
                        </div>
                    )}
                    <div className="row">
                        <div className="column -span8 justify-content-end">
                            <MeasurementsDetails showCalculatedWeight={isQA} />
                        </div>
                    </div>
                    {task.isBrandVerified && <NotificationBar message="Brand Verified" />}
                    {task.hasPremiumEditing && <NotificationBar message="This product has been flagged for premium editing." />}
                    <TaskMessages message={task.taskMessage} />
                    <Status
                        state={task.stateDescription}
                        updatedBy={task.updatedBy}
                        updatedOn={task.updatedOn}
                    />
                    {peghole}
                    <LocaleList
                        shots={shots}
                        imageClick={imageClick}
                        filterEmpty
                    />
                    <div className="editing-list">{shotHtml}</div>
                </div>
            </div>
        </PhotographyLookups>
    );
};

export default ImageEditingIndex;
